import React, { useState, useEffect, useContext } from "react";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import MButton from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import { recordTypes } from "../../API/config";
import { getEntityLinks, removeEntityLinks } from "../../API/Entity";
import { LoadingIndicator, NoAccessScreen } from "../../Component";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import LinkTable from "./Components/LinkTable";
import CreateLink from "./Components/CreateLink";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const EntityLinks = (props) => {
  const classes = useStyles();

  const { entity, hasRecordPermission, type } = props;
  const { id: entityId } = entity || {};

  const [links, setLinks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [currentLink, setCurrentLink] = useState();

  const { showMessage, showErrorMessage, showConfirmation } = useContext(
    ModalDialogContext
  );
  const { t } = useTranslation();

  const [canViewLinks, setCanViewLinks] = useState();
  const [canAddLinks, setCanAddLinks] = useState();
  const [canRemoveLinks, setCanRemoveLinks] = useState();

  useEffect(() => {
    const setPermissions = async () => {
      const { permissions = {} } = entity;
      const canUpdateRecord = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name
      );
      const canViewLinks = hasRecordPermission(
        permissions,
        type === "KYC" ?
          recordTypes.record.actions.viewKycLinks.name :
          recordTypes.record.actions.viewAmlLinks.name
      );
      setCanViewLinks(canViewLinks);
      if (canUpdateRecord) {
        const canAddLinks = hasRecordPermission(
          permissions,
          type === "KYC" ?
            recordTypes.record.actions.addKycLink.name :
            recordTypes.record.actions.addAmlLink.name
        );
        setCanAddLinks(canAddLinks);
        const canRemoveLinks = hasRecordPermission(
          permissions,
          type === "KYC" ?
            recordTypes.record.actions.removeKycLink.name :
            recordTypes.record.actions.removeAmlLink.name
        );
        setCanRemoveLinks(canRemoveLinks);
      }
    };
    setPermissions();
  }, [entity]);

  useEffect(() => {
    let isCancelled = false;
    const loadLinks = async () => {
      try {
        setLoading(true);
        const result = await getEntityLinks({ entityId, type });
        const { data = [] } = result || {};
        if (!isCancelled) {
          setLinks(data);
        }
      } catch (error) {
        showErrorMessage({ error });
      }
      if (!isCancelled) {
        setLoading(false);
        setReload(false);
      }
    };
    if (reload) {
      loadLinks();
    }
    return () => {
      isCancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    setReload(true);
  }, []);

  if (!canViewLinks) {
    return <NoAccessScreen />;
  }

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const openAddLinkDialog = () => {
    setCurrentLink(null);
    setIsDialogOpen(true);
  };

  const updateLink = (link) => {
    setCurrentLink(link);
    setIsDialogOpen(true);
  };

  const removeLink = async (link) => {
    showConfirmation({
      message: t("messages.record.links.deleteLink"),
      onPressYes: async (event) => {
        setLoading(true);
        try {
          const result = await removeEntityLinks(link.id, entityId, type);
          const { message } = result;
          showMessage({ message });
        } catch (error) {
          showErrorMessage({ error });
        }
        setReload(true);
      },
    });
  };

  const handleClose = (status) => {
    setIsDialogOpen(false);
    if (status !== "cancel") {
      setReload(true);
    }
  };

  return (
    <>
      <div className={classes.documentsBarContainer}>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6} container justify="flex-start" alignItems="center">
            <Typography component="span" className={classes.documentsBarTitle}>
              {t("labels.record.links.sectionHeader")}
            </Typography>
          </Grid>
          <Grid item xs={6} container justify="flex-end" alignItems="center">
            {canAddLinks && (
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.common.add")}
                style={{ height: 30 }}
                onClick={openAddLinkDialog}
              >
                <AddIcon />
              </MButton>
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.documentsTableContainer}>
        {isLoading ? (
          renderLoadingIndicator()
        ) : (
            <>
              <LinkTable
                data={links}
                onRowViewClick={updateLink}
                canAddLinks={canAddLinks}
                canRemoveLinks={canRemoveLinks}
                removeLink={removeLink}
              />
              {isDialogOpen && (
                <CreateLink
                  open={isDialogOpen}
                  type={type}
                  onclose={handleClose}
                  link={currentLink}
                  entity={entity}
                />
              )}
            </>
          )}
      </div>
    </>
  );
};

export default EntityLinks;
